import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppleProtectedRoute } from "@/components/AppleProtectedRoute";
import { AnalyticsProvider } from "@/hooks/useAnalytics";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Home from "@/pages/Home";

// Public pages

const Contact = lazy(() => import("./pages/Contact"));
const StarlinkBusinessInternet = lazy(() => import("./pages/StarlinkBusinessInternet"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const EndUserTerms = lazy(() => import("./pages/EndUserTerms"));
const LeasingAddendum = lazy(() => import("./pages/LeasingAddendum"));
const CameraAIAddendum = lazy(() => import("./pages/CameraAIAddendum"));
const AlarmFireAddendum = lazy(() => import("./pages/AlarmFireAddendum"));
const LLMText = lazy(() => import("./pages/LLMText"));
const Reschedule = lazy(() => import("./pages/Reschedule"));
const CustomerQuoteView = lazy(() => import("./pages/CustomerQuoteView"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

// Apple Leasing partner calculator
const AppleLeasingLogin = lazy(() => import("./pages/AppleLeasingLogin"));
const AppleLeasingSetPassword = lazy(() => import("./pages/AppleLeasingSetPassword"));
const AppleLeasingCalculator = lazy(() => import("./pages/AppleLeasingCalculator"));
const PartnerPortalApprovalAction = lazy(() => import("./pages/PartnerPortalApprovalAction"));
const AdminAppleLeasing = lazy(() => import("./pages/AdminAppleLeasing"));

// Order forms (protected — DO NOT TOUCH)
const GPSOrderForm = lazy(() => import("./pages/GPSOrderForm"));
const POTSOrderFormPage = lazy(() => import("./pages/POTSOrderForm"));
const SignAgreement = lazy(() => import("./pages/SignAgreement"));

// Admin
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminBilling = lazy(() => import("./pages/AdminBilling"));
const CustomerAccount = lazy(() => import("./pages/CustomerAccount"));
const AdminContacts = lazy(() => import("./pages/AdminContacts"));
const ContactDetail = lazy(() => import("./pages/ContactDetail"));
const AppointmentsCalendar = lazy(() => import("./pages/AppointmentsCalendar"));
const AppointmentsList = lazy(() => import("./pages/AppointmentsList"));
const AdminCalendar = lazy(() => import("./pages/AdminCalendar"));
const AppointmentDetail = lazy(() => import("./pages/AppointmentDetail"));
const Activities = lazy(() => import("./pages/Activities"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const UserApprovals = lazy(() => import("./pages/UserApprovals"));
const AdminResources = lazy(() => import("./pages/AdminResources"));
const AdminResourceEditor = lazy(() => import("./pages/AdminResourceEditor"));
const AdminQuotes = lazy(() => import("./pages/AdminQuotes"));
const AdminQuoteBuilder = lazy(() => import("./pages/AdminQuoteBuilder"));
const AdminQuoteDetail = lazy(() => import("./pages/AdminQuoteDetail"));
const AdminProducts = lazy(() => import("./pages/AdminProducts"));
const AdminAssets = lazy(() => import("./pages/AdminAssets"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminChat = lazy(() => import("./pages/AdminChat"));
const AdminTasks = lazy(() => import("./pages/AdminTasks"));
const AdminAgreements = lazy(() => import("./pages/AdminAgreements"));
const AdminResellerOrgs = lazy(() => import("./pages/AdminResellerOrgs"));
const AdminResellerOrgDetail = lazy(() => import("./pages/AdminResellerOrgDetail"));

// POTS Reseller Portal
const PortalLogin = lazy(() => import("./pages/portal/PortalLogin"));
const PortalDashboard = lazy(() => import("./pages/portal/PortalDashboard"));
const PortalOrganization = lazy(() => import("./pages/portal/PortalOrganization"));
const PortalLocations = lazy(() => import("./pages/portal/PortalLocations"));
const PortalLines = lazy(() => import("./pages/portal/PortalLines"));
const PortalTickets = lazy(() => import("./pages/portal/PortalTickets"));
const PortalUsers = lazy(() => import("./pages/portal/PortalUsers"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AnalyticsProvider>
          <ScrollToTop />
          <ErrorBoundary>
            <Suspense
              fallback={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                  }}
                >
                  Loading...
                </div>
              }
            >
              <Routes>
                {/* Public */}
                <Route path="/" element={<Home />} />
                <Route path="/pots-replacement-solutions" element={<Navigate to="/" replace />} />
                <Route path="/pots-replacement" element={<Navigate to="/" replace />} />
                <Route path="/contact-us" element={<Contact />} />
                <Route path="/starlink-business-internet" element={<StarlinkBusinessInternet />} />

                {/* Legal */}
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/legal/end-user-terms" element={<EndUserTerms />} />
                <Route path="/legal/leasing-addendum" element={<LeasingAddendum />} />
                <Route path="/legal/camera-ai-addendum" element={<CameraAIAddendum />} />
                <Route path="/legal/alarm-fire-addendum" element={<AlarmFireAddendum />} />
                <Route path="/llm.txt" element={<LLMText />} />

                {/* Customer-facing tooling */}
                <Route path="/reschedule/:appointmentId" element={<Reschedule />} />
                <Route path="/quotes/:id" element={<CustomerQuoteView />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/account" element={<CustomerAccount />} />

                {/* Apple Leasing partner calculator */}
                <Route path="/apple-leasing/login" element={<AppleLeasingLogin />} />
                <Route path="/apple-leasing/set-password" element={<AppleLeasingSetPassword />} />
                <Route path="/partner-portal-approval" element={<PartnerPortalApprovalAction />} />
                <Route path="/apple-leasing" element={<AppleProtectedRoute><AppleLeasingCalculator /></AppleProtectedRoute>} />
                <Route path="/admin/apple-leasing" element={<ProtectedRoute><AdminAppleLeasing /></ProtectedRoute>} />

                {/* Order forms (DO NOT MODIFY) */}
                <Route path="/gps-order-form" element={<GPSOrderForm />} />
                <Route path="/pots-order-form" element={<POTSOrderFormPage />} />
                <Route path="/sign/:token" element={<SignAgreement />} />

                {/* Admin */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/contacts" element={<ProtectedRoute><AdminContacts /></ProtectedRoute>} />
                <Route path="/admin/contacts/:id" element={<ProtectedRoute><ContactDetail /></ProtectedRoute>} />
                <Route path="/admin/appointments" element={<ProtectedRoute><AppointmentsCalendar /></ProtectedRoute>} />
                <Route path="/admin/appointments/list" element={<ProtectedRoute><AppointmentsList /></ProtectedRoute>} />
                <Route path="/admin/calendar" element={<ProtectedRoute><AdminCalendar /></ProtectedRoute>} />
                <Route path="/admin/appointments/:id" element={<ProtectedRoute><AppointmentDetail /></ProtectedRoute>} />
                <Route path="/admin/activities" element={<ProtectedRoute><Activities /></ProtectedRoute>} />
                <Route path="/admin/user-approvals" element={<ProtectedRoute><UserApprovals /></ProtectedRoute>} />
                <Route path="/admin/user-management" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
                <Route path="/admin/resources" element={<ProtectedRoute><AdminResources /></ProtectedRoute>} />
                <Route path="/admin/resources/new" element={<ProtectedRoute><AdminResourceEditor /></ProtectedRoute>} />
                <Route path="/admin/resources/edit/:id" element={<ProtectedRoute><AdminResourceEditor /></ProtectedRoute>} />
                <Route path="/admin/quotes" element={<ProtectedRoute><AdminQuotes /></ProtectedRoute>} />
                <Route path="/admin/quotes/new" element={<ProtectedRoute><AdminQuoteBuilder /></ProtectedRoute>} />
                <Route path="/admin/quotes/:id" element={<ProtectedRoute><AdminQuoteDetail /></ProtectedRoute>} />
                <Route path="/admin/quotes/:id/edit" element={<ProtectedRoute><AdminQuoteBuilder /></ProtectedRoute>} />
                <Route path="/admin/products" element={<ProtectedRoute><AdminProducts /></ProtectedRoute>} />
                <Route path="/admin/assets" element={<ProtectedRoute><AdminAssets /></ProtectedRoute>} />
                <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
                <Route path="/admin/chat" element={<ProtectedRoute><AdminChat /></ProtectedRoute>} />
                <Route path="/admin/tasks" element={<ProtectedRoute><AdminTasks /></ProtectedRoute>} />
                <Route path="/admin/agreements" element={<ProtectedRoute><AdminAgreements /></ProtectedRoute>} />
                <Route path="/admin/billing" element={<ProtectedRoute><AdminBilling /></ProtectedRoute>} />
                <Route path="/admin/reseller-orgs" element={<ProtectedRoute><AdminResellerOrgs /></ProtectedRoute>} />
                <Route path="/admin/reseller-orgs/:id" element={<ProtectedRoute><AdminResellerOrgDetail /></ProtectedRoute>} />

                {/* POTS Reseller Portal */}
                <Route path="/pots/portal" element={<Navigate to="/pots/portal/dashboard" replace />} />
                <Route path="/pots/portal/login" element={<PortalLogin />} />
                <Route path="/pots/portal/dashboard" element={<PortalDashboard />} />
                <Route path="/pots/portal/organization" element={<PortalOrganization />} />
                <Route path="/pots/portal/locations" element={<PortalLocations />} />
                <Route path="/pots/portal/lines" element={<PortalLines />} />
                <Route path="/pots/portal/tickets" element={<PortalTickets />} />
                <Route path="/pots/portal/users" element={<PortalUsers />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </AnalyticsProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
